import React, { useState, useEffect } from "react";
import companyService from "@/services/companyService";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";

function rffrArrayToState(arr, active) {
  const get = (name) => arr?.find(e => e.settingName === name)?.value || "";
  return {
    enabled: active ? "enabled" : "disabled",
    providerCode: get("Provider code"),
    orgName: get("Organisation name"),
    category: get("Category"),
    iso27001Version: get("ISO27001 version"),
  };
}
function rffrStateToArray(state) {
  return [
    { settingName: "Provider code", value: state.providerCode },
    { settingName: "Organisation name", value: state.orgName },
    { settingName: "Category", value: state.category },
    { settingName: "ISO27001 version", value: state.iso27001Version },
  ];
}

export default function RFFRModule() {
  const [rffr, setRffr] = useState({
    enabled: "disabled",
    providerCode: "",
    orgName: "",
    category: "",
    iso27001Version: "",
  });
  const [rffrInitial, setRffrInitial] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchISM = async () => {
      setIsLoading(true);
      try {
        const settings = await companyService.getCompanySettings(undefined, "complianceModules");
        let state;
        if (settings?.["ism"]) {
          const ism = settings["ism"];
          state = {
            enabled: ism.Enabled ? "enabled" : "disabled",
            providerCode: ism.RFFRProviderCode ?? "",
            orgName: ism.RFFROrganisationName ?? "",
            category: ism.RFFRCategory ?? "",
            iso27001Version: ism.ISO27001Version ?? "",
          };
        } else {
          // fallback: blank state
          state = {
            enabled: "disabled",
            providerCode: "",
            orgName: "",
            category: "",
            iso27001Version: "",
          };
        }
        setRffr(state);
        setRffrInitial(state);
      } catch (err) {
        console.error("Failed to fetch ISM/RFFR settings:", err);
        toast.error("Failed to load ISM/RFFR settings");
      } finally {
        setIsLoading(false);
      }
    };
    fetchISM();
  }, []);

  const rffrChanged = JSON.stringify(rffr) !== JSON.stringify(rffrInitial);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await companyService.saveCompanySettings(undefined, "complianceModules", {
        "ism": {
          Enabled: rffr.enabled === "enabled",
          ISO27001Version: rffr.iso27001Version,
          RFFRCategory: rffr.category,
          RFFROrganisationName: rffr.orgName,
          RFFRProviderCode: rffr.providerCode
        }
      });
      setRffrInitial({ ...rffr });
      toast.success("ISM/RFFR settings saved");
    } catch (err) {
      console.error("Failed to save ISM/RFFR settings:", err);
      toast.error("Failed to save ISM/RFFR settings");
    } finally {
      setIsSaving(false);
    }
  };


  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <label className="block text-sm font-medium text-foreground">Module Enabled</label>
        <Select
          value={rffr.enabled}
          onValueChange={val => setRffr(v => ({ ...v, enabled: val }))}
          disabled={isLoading || isSaving}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="enabled">Enabled</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Indicates whether the RFFR Module is enabled or disabled.<br />For example, if set to "Enabled," the RFFR Module is active.
        </p>
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-foreground">Provider code:</label>
        <input
          className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
          value={rffr.providerCode}
          onChange={e => setRffr(v => ({ ...v, providerCode: e.target.value }))}
          disabled={isLoading || isSaving}
          placeholder="RFFR12345"
        />
        <p className="text-xs text-muted-foreground">
          Enter the unique identification code assigned to your organization for Right Fit for Risk (RFFR) accreditation. This code helps verify your compliance status with the Department of Education, Skills and Employment (DESE)!<br />For example "RFFR12345"
        </p>
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-foreground">Organisation name:</label>
        <input
          className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
          value={rffr.orgName}
          onChange={e => setRffr(v => ({ ...v, orgName: e.target.value }))}
          disabled={isLoading || isSaving}
          placeholder="Pondstone Partners Pty Ltd"
        />
        <p className="text-xs text-muted-foreground">
          Please provide the full legal name of your organization as registered with the RFFR program. This name will be used for official documentation and audits related to your accreditation.<br />For example: "Pondstone Partners Pty Ltd"
        </p>
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-foreground">Category</label>
        <Select
          value={rffr.category}
          onValueChange={val => setRffr(v => ({ ...v, category: val }))}
          disabled={isLoading || isSaving}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="C1">Category 1</SelectItem>
            <SelectItem value="C2A">Category 2A</SelectItem>
          </SelectContent>
        </Select>
        <ul className="text-xs text-muted-foreground pl-4 list-disc">
          <li>Category 1: Requires independent third-party assessment to demonstrate compliance with ISO 27001 and the Australian Government Information Security Manual.</li>
          <li>Category 2A: Self-assessed. Providers document and submit reports on their ISO 27001 compliance without external evaluation.</li>
        </ul>
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-foreground">ISO27001 version</label>
        <Select
          value={rffr.iso27001Version}
          onValueChange={val => setRffr(v => ({ ...v, iso27001Version: val }))}
          disabled={isLoading || isSaving}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select version" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="2022">2022</SelectItem>
            <SelectItem value="2013">2013</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Specify the version of ISO 27001 your organization has implemented for RFFR compliance. Ensure that the version includes any customizations required by the Australian Government's Information Security Manual (ISM) controls.
        </p>
      </div>
      <div className="pt-4">
        <button
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors flex items-center gap-2"
          onClick={handleSave}
          disabled={isSaving || !rffrChanged}
        >
          {isSaving && (
            <svg className="animate-spin h-4 w-4 mr-2 text-primary-foreground" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
          )}
          Save RFFR Settings
        </button>
      </div>
    </div>
  );
}
